import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { businessApi } from '../lib/api';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  LayoutDashboard,
  Users,
  Building2,
  BookmarkCheck,
  Menu,
  LogOut,
  Coffee,
  Target,
  ChevronLeft,
  ChevronRight,
  Settings,
} from 'lucide-react';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, adminOnly: false },
  { path: '/targets', label: 'Targets', icon: Target, adminOnly: false },
  { path: '/layouts', label: 'Saved Layouts', icon: BookmarkCheck, adminOnly: false },
  { path: '/users', label: 'Users', icon: Users, adminOnly: true },
  { path: '/businesses', label: 'Businesses', icon: Building2, adminOnly: true },
  { path: '/integrations', label: 'Integrations', icon: Settings, adminOnly: true },
];

const Sidebar = ({ mobile = false, onClose, collapsed = false, onToggle }) => {
  const location = useLocation();
  const { user, isAdmin } = useAuthStore();

  const filteredNavItems = navItems.filter(
    (item) => !item.adminOnly || isAdmin()
  );

  return (
    <div className={`flex flex-col h-full bg-card ${mobile ? '' : collapsed ? 'w-20' : 'w-64'}`}>
      {/* Logo */}
      <div className={`border-b border-border ${collapsed && !mobile ? 'p-3' : 'p-6'}`}>
        <Link to="/" className="flex items-center gap-3" onClick={onClose}>
          <div className="w-10 h-10 rounded-xl bg-coffee-espresso flex items-center justify-center flex-shrink-0">
            <Coffee className="w-5 h-5 text-coffee-cream" />
          </div>
          <div className={`overflow-hidden transition-[opacity,width] duration-200 ${collapsed && !mobile ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
            <h1 className="font-serif text-lg font-semibold text-coffee-espresso whitespace-nowrap">
              Coffee Analytics
            </h1>
            <p className="text-xs text-muted-foreground whitespace-nowrap">Sales Dashboard</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 space-y-1 overflow-hidden ${collapsed && !mobile ? 'p-2' : 'p-4'}`}>
        <TooltipProvider delayDuration={0}>
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            const linkContent = (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                  isActive
                    ? 'bg-coffee-espresso text-coffee-cream shadow-card'
                    : 'text-coffee-espresso hover:bg-secondary'
                } ${collapsed && !mobile ? 'justify-center px-3' : ''}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className={`font-medium whitespace-nowrap overflow-hidden transition-[opacity,width] duration-200 ${collapsed && !mobile ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
                  {item.label}
                </span>
              </Link>
            );

            if (collapsed && !mobile) {
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    {linkContent}
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return linkContent;
          })}
        </TooltipProvider>
      </nav>

      {/* Collapse Toggle (Desktop only) */}
      {!mobile && onToggle && (
        <div className="p-2 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className={`w-full rounded-xl transition-all duration-200 ${collapsed ? 'justify-center' : 'justify-start'}`}
            data-testid="sidebar-toggle"
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 mr-2" />
                <span>Collapse</span>
              </>
            )}
          </Button>
        </div>
      )}

      {/* User info */}
      <div className={`p-4 border-t border-border ${collapsed && !mobile ? 'px-2' : ''}`}>
        <div className={`flex items-center gap-3 ${collapsed && !mobile ? 'justify-center' : 'px-2'}`}>
          <Avatar className="w-9 h-9 flex-shrink-0">
            <AvatarFallback className="bg-secondary text-coffee-espresso text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className={`flex-1 min-w-0 overflow-hidden transition-[opacity,width] duration-200 ${collapsed && !mobile ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
            <p className="text-sm font-medium text-coffee-espresso truncate whitespace-nowrap">
              {user?.name}
            </p>
            <p className="text-xs text-muted-foreground capitalize whitespace-nowrap">
              {user?.role?.name?.replace('_', ' ')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout, selectedBusinessId, setSelectedBusiness } = useAuthStore();
  const [businesses, setBusinesses] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await businessApi.getAll();
        setBusinesses(response.data);
      } catch (error) {
        console.error('Failed to fetch businesses:', error);
      }
    };
    fetchBusinesses();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentBusiness = businesses.find((b) => b.id === selectedBusinessId);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 sidebar border-r border-border bg-card z-40 transition-[width] duration-300 ease-in-out ${
          sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'
        }`}
        style={{ willChange: 'width' }}
      >
        <Sidebar 
          collapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b border-border">
        <div className="flex items-center justify-between px-4 h-16">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <Sidebar mobile onClose={() => setMobileMenuOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2">
            <Coffee className="w-5 h-5 text-coffee-espresso" />
            <span className="font-serif font-semibold text-coffee-espresso">
              Coffee Analytics
            </span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="user-menu-btn">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-secondary text-coffee-espresso text-xs">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn-mobile">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <main 
        className={`flex-1 min-w-0 transition-[margin-left] duration-300 ease-in-out ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}
        style={{ willChange: 'margin-left' }}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-card/80 backdrop-blur-md border-b border-border hidden lg:block">
          <div className="flex items-center justify-between px-6 h-16">
            {/* Business Selector */}
            <div className="flex items-center gap-4">
              <Select
                value={selectedBusinessId || ''}
                onValueChange={setSelectedBusiness}
              >
                <SelectTrigger
                  className="w-[280px] bg-white"
                  data-testid="business-selector"
                >
                  <SelectValue placeholder="Select Business">
                    {currentBusiness?.name || 'Select Business'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {businesses.map((business) => (
                    <SelectItem key={business.id} value={business.id}>
                      {business.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2"
                  data-testid="user-menu-desktop"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-secondary text-coffee-espresso text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline text-sm font-medium">
                    {user?.name}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-accent capitalize mt-1">
                    {user?.role?.name?.replace('_', ' ')}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Mobile Business Selector */}
        <div className="lg:hidden pt-16 px-4 py-3 bg-card border-b border-border">
          <Select
            value={selectedBusinessId || ''}
            onValueChange={setSelectedBusiness}
          >
            <SelectTrigger className="w-full bg-white" data-testid="business-selector-mobile">
              <SelectValue placeholder="Select Business">
                {currentBusiness?.name || 'Select Business'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {businesses.map((business) => (
                <SelectItem key={business.id} value={business.id}>
                  {business.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Page Content */}
        <div className="p-4 md:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
